import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Banner,
  BarRow,
  Card,
  Chip,
  Empty,
  Field,
  Modal,
  SectionHead,
  Select,
  StagePill,
  Stat,
  ThemeSwitch,
  navigation,
} from './components/ui';
import { UserAvatar } from './components/UserAvatar';
import { BrandMark } from './components/BrandMark';
import {
  DEFAULT_SPRINT_START,
  DEFAULT_TOTAL_DAYS,
  daysUntilStart,
  formatSprintLabel,
} from './lib/sprint';
import { useStore, useTheme } from './state/store';
import { WorkspaceView, useSprintDay } from './pages/WorkspaceView';
import type { Session } from './state/api';
import type { Partner, ThemeMode } from './types/anchor';

const STAGES = [
  'Identified',
  'Researched',
  'Contacted',
  'Discovery',
  'Fit confirmed',
  'Solution / Architecture',
  'Commercial / Support package',
  'Legal / Compliance',
  'Committed',
  'Active',
  'Expanding',
  'Parked',
  'Rejected',
  'Pilot ready',
];

function UserMenu({
  session,
  themeMode,
  themeHydrated,
  onThemeChange,
  onManageProfile,
  onLogout,
}: {
  session: Session;
  themeMode: ThemeMode;
  themeHydrated: boolean;
  onThemeChange: (v: ThemeMode) => void;
  onManageProfile: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const displayName = session.displayName || session.email || 'Account';

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        type="button"
        className="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title={displayName}
        onClick={() => setOpen((v) => !v)}
      >
        <UserAvatar url={session.profilePictureUrl} name={displayName} size={32} />
      </button>
      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-head">
            <UserAvatar url={session.profilePictureUrl} name={displayName} size={40} />
            <div className="user-menu-identity">
              <strong>{displayName}</strong>
              <span className="dim tiny">{session.email}</span>
              {session.organization?.name && (
                <span className="dim tiny">{session.organization.name}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              onManageProfile();
              setOpen(false);
            }}
          >
            Settings
          </button>
          {themeHydrated && (
            <div className="user-menu-section">
              <div className="user-menu-label">Appearance</div>
              <ThemeSwitch value={themeMode} onChange={onThemeChange} />
            </div>
          )}
          <div className="user-menu-sep" />
          <button
            type="button"
            className="user-menu-item danger"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function App({
  onLogout,
  session,
}: {
  onLogout: () => void;
  session: Session;
}) {
  const { state, status, error, createPartner, updatePartner, exportSnapshot, importSnapshot } = useStore();
  const [active, setActive] = useState('Command');
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState<Partner | null>(null);
  const [themeMode, setThemeMode] = useState<ThemeMode>('system');
  const [themeHydrated, setThemeHydrated] = useState(false);
  const sprintStart = state.meta?.sprintStart || DEFAULT_SPRINT_START;
  const totalDays = state.meta?.totalDays || DEFAULT_TOTAL_DAYS;
  const todayLabel = useSprintDay(sprintStart, totalDays);
  const untilStart = daysUntilStart(sprintStart);

  useTheme(themeHydrated ? themeMode : null);

  const partners = state.partners || [];
  const filteredPartners = partners.filter((p) =>
    `${p.name} ${p.type}`.toLowerCase().includes(query.toLowerCase()),
  );
  const strategicLayers = (state.strategicNetwork?.layers || []).slice(0, 6);
  const tactical = state.tactical?.metrics || [];
  const daysLeft = Math.max(0, totalDays - 1 - todayLabel);
  const pctElapsed =
    untilStart > 0 ? 0 : Math.max(2, ((todayLabel + 1) / totalDays) * 100);

  useEffect(() => {
    queueMicrotask(() => {
      const saved = localStorage.getItem('anchor-theme') || localStorage.getItem('anchor.theme');
      if (saved === 'system' || saved === 'light' || saved === 'dark') setThemeMode(saved);
      setThemeHydrated(true);
    });
  }, []);

  async function addPartner(form: FormData) {
    const name = String(form.get('name') || '').trim();
    if (!name) return;
    await createPartner({
      name,
      type: String(form.get('type') || 'Strategic'),
      next: String(form.get('ask') || 'Define a concrete ask'),
      region: String(form.get('region') || 'Global'),
      rank: partners.length + 1,
      score: 0,
      evidence: 'P0',
      stage: 'Identified',
      signal: 'watch',
      strategic: 0,
      due: 'Unscheduled',
    });
    setShowAdd(false);
  }

  function exportData(kind: 'json' | 'csv') {
    const snapshot = exportSnapshot();
    const body =
      kind === 'json'
        ? JSON.stringify({ exportedAt: new Date().toISOString(), ...snapshot }, null, 2)
        : [
            'name,type,stage,evidence,tactical_score,next_action,due',
            ...partners.map((p) =>
              [p.name, p.type, p.stage, p.evidence, p.score, p.next, p.due]
                .map((v) => `"${String(v).replaceAll('"', '""')}"`)
                .join(','),
            ),
          ].join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(
      new Blob([body], { type: kind === 'json' ? 'application/json' : 'text/csv' }),
    );
    link.download = `anchor-partners.${kind}`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  function importData(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as typeof state;
        void importSnapshot(parsed);
      } catch {
        // ignore invalid JSON
      }
    };
    reader.readAsText(file);
  }

  if (status === 'loading') {
    return (
      <div className="boot">
        <div className="boot-inner">
          <BrandMark size={40} tiled className="boot-mark" />
          <h1>Anchor</h1>
          <div className="tag">Loading partnership workspace…</div>
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="boot">
        <div className="boot-inner">
          <BrandMark size={40} tiled className="boot-mark" />
          <h1>Cannot reach Anchor API</h1>
          <div className="boot-error">{error}</div>
          <p className="dim">Start the API on :4646 (`npm run dev:api`) or run `npm run local`.</p>
        </div>
      </div>
    );
  }

  const navItems = navigation;

  return (
    <div className="app">
      <nav className="nav">
        <div className="nav-brand">
          <div className="nav-brand-row">
            <BrandMark size={22} className="nav-brand-mark" />
            <div>
              <h1>Anchor</h1>
              <div className="tag">Partnership OS</div>
            </div>
          </div>
        </div>

        <div className="nav-day">
          <div className="big">
            DAY {todayLabel} / {totalDays}
          </div>
          <div className="sub">
            {untilStart > 0
              ? `Day 0 ${formatSprintLabel(sprintStart)} · starts in ${untilStart} day${untilStart === 1 ? '' : 's'}`
              : `${formatSprintLabel(sprintStart)} → ${formatSprintLabel(state.meta?.sprintEnd || sprintStart)} · ${daysLeft} days left`}
          </div>
          <div className="bar">
            <span style={{ width: `${pctElapsed}%` }} />
          </div>
        </div>

        <div className="nav-items">
          {navItems.map((item, i) => (
            <button
              key={item.label}
              type="button"
              className={`nav-item ${active === item.label ? 'active' : ''}`}
              onClick={() => setActive(item.label)}
            >
              <span className="idx">{String(i + 1).padStart(2, '0')}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        <div className="nav-foot">
          <span className="lock-chip">STRATEGY LOCKED UNTIL 26 SEP 2026</span>
        </div>
      </nav>

      <main className="main">
        <header className="topbar">
          <h2>{active}</h2>
          <Chip>
            {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
          </Chip>
          <div className="spacer" />
          <label className="row tight" style={{ margin: 0 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search partners"
              style={{ width: 160, marginBottom: 0 }}
              aria-label="Search partners"
            />
          </label>
          <button type="button" className="btn sm primary" onClick={() => setShowAdd(true)}>
            + Add partner
          </button>
          <UserMenu
            session={session}
            themeMode={themeMode}
            themeHydrated={themeHydrated}
            onThemeChange={setThemeMode}
            onManageProfile={() => setActive('Settings')}
            onLogout={onLogout}
          />
        </header>

        <div className="page">
          {active === 'Command' ? (
            <>
              <div className="page-head">
                <h2>Partnership command</h2>
                <div className="sub">Tactical proof. Strategic leverage.</div>
              </div>

              <div className="grid g2">
                <Card
                  title="Tactical · Day 1–45"
                  right={
                    <button type="button" className="btn sm ghost" onClick={() => setActive('Tactical 45')}>
                      Open
                    </button>
                  }
                >
                  <p className="dim" style={{ marginTop: 0 }}>
                    Accelerate D001, Infrastructure revenue, Saudi validation, or runway.
                  </p>
                  <div className="grid g2" style={{ gap: 10 }}>
                    {tactical.map((item) => (
                      <Stat
                        key={item.area}
                        label={item.area}
                        value={item.metric}
                        foot={item.detail}
                        size="sm"
                      />
                    ))}
                  </div>
                </Card>

                <Card
                  title="Strategic · 3–36 months"
                  right={
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => setActive('Strategic Network')}
                    >
                      Open
                    </button>
                  }
                >
                  <p className="dim" style={{ marginTop: 0 }}>
                    Build reusable advantage across markets without becoming hostage to a partner.
                  </p>
                  <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
                    <Stat label="Network health" value={state.strategicNetwork?.health ?? 0} size="lg" />
                    <span className="dim tiny">/ 100 · 3 critical gaps</span>
                  </div>
                </Card>
              </div>

              <div className="section">
                <SectionHead
                  title="Monday focus"
                  hint="Tactical Top 5"
                  right={
                    <button type="button" className="btn sm ghost" onClick={() => setActive('Tactical 45')}>
                      Open Tactical 45
                    </button>
                  }
                />
                <div className="grid g-2-1">
                  <Card flush title="Partners">
                    <div className="table-wrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Partner</th>
                            <th className="num">Score</th>
                            <th>Evidence</th>
                            <th>Stage</th>
                            <th>Next move</th>
                            <th>Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPartners.slice(0, 5).map((partner, idx) => (
                            <tr
                              key={partner.id || partner.name}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelected(partner)}
                            >
                              <td className="num">{partner.rank ?? idx + 1}</td>
                              <td>
                                <strong>{partner.name}</strong>
                                <div className="dim tiny">{partner.type}</div>
                              </td>
                              <td className="num">{partner.score}</td>
                              <td>
                                <StagePill value={partner.evidence} />
                              </td>
                              <td>{partner.stage}</td>
                              <td>{partner.next}</td>
                              <td className="dim">{partner.due}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredPartners.length === 0 && (
                        <Empty title="No partners match">Try a different search.</Empty>
                      )}
                    </div>
                  </Card>

                  <div className="grid" style={{ gap: 14, alignContent: 'start' }}>
                    {partners[0] && (
                      <Card title="Closest to real leverage" className="hero">
                        <div className="row" style={{ marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div className="dim tiny">{partners[0].name}</div>
                            <strong style={{ fontSize: 15 }}>{partners[0].next}</strong>
                          </div>
                          <StagePill value={partners[0].evidence} />
                        </div>
                        <button
                          type="button"
                          className="btn primary block"
                          onClick={() => setSelected(partners[0])}
                        >
                          Move this partner
                        </button>
                      </Card>
                    )}

                    <Card title="The Anchor" sub="AI partnership strategist" right={<Chip tone="accent">Live</Chip>}>
                      <p style={{ margin: 0, lineHeight: 1.5 }}>
                        “Your strongest relationship is one gate from P5. Close the architecture question before
                        adding another ecosystem meeting.”
                      </p>
                    </Card>

                    <Card
                      title="Coverage by layer"
                      right={<Chip>{state.strategicNetwork?.health ?? 0} / 100</Chip>}
                    >
                      {strategicLayers.map((layer) => (
                        <BarRow
                          key={layer.label}
                          label={`${layer.label} · ${layer.state}`}
                          actual={layer.value}
                          suffix=""
                        />
                      ))}
                    </Card>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <WorkspaceView
              active={active}
              state={state}
              partners={partners}
              onAdd={() => setShowAdd(true)}
              onSelect={setSelected}
              onNavigate={setActive}
              onExport={exportData}
              onImport={importData}
              themeMode={themeMode}
              onThemeChange={setThemeMode}
            />
          )}
        </div>
      </main>

      {showAdd && (
        <Modal
          title="Add partner"
          sub="New relationship"
          onClose={() => setShowAdd(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
              <button type="submit" form="add-partner-form" className="btn primary">
                Add to pipeline
              </button>
            </>
          }
        >
          <form
            id="add-partner-form"
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              void addPartner(new FormData(e.currentTarget));
            }}
          >
            <Field label="Organization">
              <input name="name" autoFocus required placeholder="Organization name" />
            </Field>
            <div className="grid g2">
              <Field label="Partner type">
                <select name="type" defaultValue="Ecosystem">
                  <option>Ecosystem</option>
                  <option>Regulated execution</option>
                  <option>Distribution</option>
                  <option>Infrastructure</option>
                  <option>Institutional</option>
                  <option>Strategic capital</option>
                </select>
              </Field>
              <Field label="Region">
                <input name="region" placeholder="Dubai, Saudi, Global…" />
              </Field>
            </div>
            <Field label="Current concrete ask">
              <textarea name="ask" placeholder="What exactly do we want them to do?" />
            </Field>
            <Banner tone="warn">
              New partners start at IDENTIFIED / P0. A logo or meeting earns no leverage credit.
            </Banner>
          </form>
        </Modal>
      )}

      {selected && (
        <div className="drawer-back" onMouseDown={() => setSelected(null)}>
          <aside className="drawer" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="modal-head">
              <div>
                <div className="dim tiny" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Partner record
                </div>
                <h3>{selected.name}</h3>
              </div>
              <div className="spacer" />
              <button type="button" className="btn ghost sm" onClick={() => setSelected(null)}>
                Close
              </button>
            </header>
            <div className="modal-body">
              <div className="row tight" style={{ marginBottom: 14 }}>
                <Chip>{selected.type}</Chip>
                <StagePill value={selected.evidence} />
                <Chip tone="dark">{selected.stage}</Chip>
              </div>
              <Banner>
                What does this relationship allow Blocks to do that Blocks could not do alone?
              </Banner>
              <div className="mt14">
                <Stat label="Tactical value" value={`${selected.score} / 100`} size="sm" />
                <Stat
                  label="Strategic value"
                  value={`${selected.strategic || Math.max(45, selected.score - 4)} / 100`}
                  size="sm"
                />
                <Stat label="Current ask" value={selected.next} size="sm" />
                <Stat label="Next action due" value={selected.due} size="sm" />
              </div>
              <Select
                label="Evidence level"
                value={selected.evidence}
                onChange={(evidence) => {
                  void updatePartner(selected.id, { evidence }).then((record) => setSelected(record));
                }}
                options={[0, 1, 2, 3, 4, 5].map((n) => `P${n}`)}
              />
              <Select
                label="Pipeline stage"
                value={selected.stage}
                onChange={(stage) => {
                  void updatePartner(selected.id, { stage }).then((record) => setSelected(record));
                }}
                options={STAGES}
              />
              <button type="button" className="btn primary block" onClick={() => setSelected(null)}>
                Save evidence update
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
